import { phone } from '@parsina/framework';

import { UNPROVISIONED, statusLabelKey } from '../constants/subscription';
import { planLabel } from './format';
import { createdMs } from './clinicsView';

/**
 * «خروجی اکسل» of the clinics list — the rows, as a sheet.
 *
 * LATIN DIGITS AND NUMERIC DATES WHATEVER THE LOCALE, same rule as the pricing
 * screen's CSV: the on-screen formatting (۱۴۰۵/۸/۷, Persian digits) must not leak
 * into a file whose next stop is a filter, a sort or a pivot. Each registration
 * is written as BOTH calendars, so «۷ آبان» can be matched against the Gregorian
 * date a bank statement or a log carries without anyone converting by hand.
 *
 * THE DAY IS A TEHRAN DAY, like the range filter that selects the rows
 * (`demoRequestsView.dateRangeFilter`): the back office runs on Tehran time, and a
 * clinic registered at 22:00 UTC belongs to the NEXT Tehran date. Iran has kept
 * +03:30 all year since 2022. This is the OPERATOR's day, not the clinic's wall
 * clock — a clinic's own times follow its `timezone` (CLAUDE.md, «Clinic wall
 * clock»).
 */
const TEHRAN = 'Asia/Tehran';

const GREGORIAN = new Intl.DateTimeFormat('en-US', {
  timeZone: TEHRAN, calendar: 'gregory', numberingSystem: 'latn',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const JALALI = new Intl.DateTimeFormat('en-US-u-ca-persian-nu-latn', {
  timeZone: TEHRAN, year: 'numeric', month: '2-digit', day: '2-digit',
});

const parts = (formatter, ms) => Object.fromEntries(
  formatter.formatToParts(new Date(ms)).map((p) => [p.type, p.value]),
);

/** `{ jalali: '1405/08/07', gregorian: '2026-10-29', time: '10:28' }`, or empty strings. */
export function registrationStamp(row) {
  const ms = createdMs(row);
  if (ms == null) return { jalali: '', gregorian: '', time: '' };
  const g = parts(GREGORIAN, ms);
  const j = parts(JALALI, ms);
  return {
    jalali: `${j.year}/${j.month}/${j.day}`,
    gregorian: `${g.year}-${g.month}-${g.day}`,
    time: `${g.hour}:${g.minute}`,
  };
}

/**
 * @param rows  the clinics to write, in the order they should appear
 * @param t     the panel's translator — headers and status words follow the locale
 * @returns { header, rows } for `buildXlsx`
 */
export function clinicsExportTable(rows, { t }) {
  const header = [
    t('clinics.export.columns.name'),
    t('clinics.export.columns.type'),
    t('clinics.export.columns.phone'),
    t('clinics.export.columns.jalali'),
    t('clinics.export.columns.gregorian'),
    t('clinics.export.columns.time'),
    t('clinics.export.columns.active'),
    t('clinics.export.columns.status'),
    t('clinics.export.columns.plan'),
    t('clinics.export.columns.currency'),
    t('clinics.export.columns.id'),
  ];

  const body = (rows || []).map((r) => {
    const stamp = registrationStamp(r);
    return [
      r.name || '',
      r.clinicType || '',
      // The national form the table shows; text, so the leading 0 survives.
      r.officePhone ? phone.forDisplay(r.officePhone) : '',
      stamp.jalali,
      stamp.gregorian,
      stamp.time,
      r.active ? t('common.value.yes') : t('common.value.no'),
      t(statusLabelKey(r.hasSubscription ? r.subscriptionStatus : UNPROVISIONED)),
      // «بدون اشتراک» has no plan; `planLabel` would answer with a dash.
      r.hasSubscription ? planLabel(r, t) : '',
      r.currency || '',
      r.id || '',
    ];
  });

  return { header, rows: body };
}

/**
 * `clinics-2026-10-29_2026-10-30.xlsx` — the picker days, so the file says what
 * it covers; `clinics-from-…` / `clinics-until-…` for a half-open range and
 * `clinics.xlsx` when none is set. A reversed range is written in order, as the
 * filter reads it.
 */
export function clinicsExportFileName({ from, to } = {}) {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  const a = iso.test(from || '') ? from : '';
  const b = iso.test(to || '') ? to : '';
  if (a && b) return `clinics-${[a, b].sort().join('_')}.xlsx`;
  if (a) return `clinics-from-${a}.xlsx`;
  if (b) return `clinics-until-${b}.xlsx`;
  return 'clinics.xlsx';
}
